import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { ChecksheetAssignment } from './checksheet-assignment.model';
import { CompletionStatus } from '@completion/enums';
import { StatusCircleModule } from '../status-circle/status-circle.module';

export interface AssignmentNavigationEvent {
  type: 'cp' | 'mcp' | 'checksheet';
  assignment: ChecksheetAssignment;
}

export interface TableColumnConfig {
  key: string;
  label: string;
  width?: string;
  minWidth?: string;
}

@Component({
  selector: 'app-checksheet-assignments-table',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, TooltipModule, TagModule, ProgressBarModule, StatusCircleModule],
  templateUrl: './checksheet-assignments-table.component.html',
  styleUrls: ['./checksheet-assignments-table.component.scss'],
})
export class ChecksheetAssignmentsTableComponent {
  @Input() assignments: ChecksheetAssignment[] = [];
  @Input() displayColumns: string[] = [
    'cpNumber',
    'mcpNumber',
    'tagNumber',
    'checksheetNumber',
    'checksheetType',
    'status',
    'siteNumber',
    'companyNumber',
    'phaseNumber',
    'missingCertificates',
  ];

  @Output() navigate = new EventEmitter<AssignmentNavigationEvent>();

  columnConfig: Record<string, TableColumnConfig> = {
    cpNumber: { key: 'cpNumber', label: 'CommPkg', width: '94px', minWidth: '78px' },
    mcpNumber: { key: 'mcpNumber', label: 'MC Package', width: '110px', minWidth: '90px' },
    tagNumber: { key: 'tagNumber', label: 'Tag', width: '130px', minWidth: '110px' },
    checksheetNumber: { key: 'checksheetNumber', label: 'Checksheet', width: '95px', minWidth: '82px' },
    checksheetType: { key: 'checksheetType', label: 'Type', width: '65px', minWidth: '55px' },
    status: { key: 'status', label: 'Status', width: '70px', minWidth: '60px' },
    progress: { key: 'progress', label: 'Progress', width: '95px', minWidth: '82px' },
    punchCount: { key: 'punchCount', label: 'Punches', width: '100px', minWidth: '82px' },
    signed: { key: 'signed', label: 'Signed', width: '120px', minWidth: '96px' },
    verified: { key: 'verified', label: 'Verified', width: '120px', minWidth: '96px' },
    siteNumber: { key: 'siteNumber', label: 'Site', width: '65px', minWidth: '55px' },
    companyNumber: { key: 'companyNumber', label: 'Company', width: '80px', minWidth: '70px' },
    phaseNumber: { key: 'phaseNumber', label: 'Phase', width: '65px', minWidth: '55px' },
    missingCertificates: { key: 'missingCertificates', label: 'Missing', width: '120px', minWidth: '100px' },
  };

  navToCp(assignment: ChecksheetAssignment): void {
    if (!assignment.cpId) return;
    this.navigate.emit({ type: 'cp', assignment });
  }

  navToMcp(assignment: ChecksheetAssignment): void {
    if (!assignment.cpId || !assignment.mcpId) return;
    this.navigate.emit({ type: 'mcp', assignment });
  }

  navToChecksheet(assignment: ChecksheetAssignment): void {
    if (!assignment.cpId || !assignment.id) return;
    this.navigate.emit({ type: 'checksheet', assignment });
  }

  isVoided(assignment: ChecksheetAssignment): boolean {
    return assignment.engineeringStatus === 'V';
  }

  getCompletionStatus(status: string | null | undefined): CompletionStatus {
    if (!status) return CompletionStatus.OS;
    return CompletionStatus[status as keyof typeof CompletionStatus];
  }

  progressPct(_assignment: ChecksheetAssignment): number {
    // For non-certified assignments, progress is not available, so return 0
    return 0;
  }

  hasPunches(assignment: ChecksheetAssignment): boolean {
    return (assignment.punchCount ?? 0) > 0 && this.getPunchIds(assignment).length > 0;
  }

  punchLabel(assignment: ChecksheetAssignment): string {
    const count = assignment.punchCount ?? 0;
    return count === 1 ? '1 punch' : `${count} punches`;
  }

  punchTooltip(assignment: ChecksheetAssignment): string {
    if (!this.hasPunches(assignment)) {
      return '';
    }
    return this.punchLabel(assignment);
  }

  punchStatus(assignment: ChecksheetAssignment): CompletionStatus {
    if (!this.hasPunches(assignment)) {
      return CompletionStatus.OS;
    }
    return CompletionStatus.PB;
  }

  missingLabel(assignment: ChecksheetAssignment): string {
    const missing: string[] = [];
    if (assignment.missingMcc) missing.push('MCC');
    if (assignment.missingRfcc) missing.push('RFCC');
    if (assignment.missingRfoc) missing.push('RFOC');
    return missing.join(', ');
  }

  private getPunchIds(assignment: ChecksheetAssignment): number[] {
    if (!assignment.punchIdsCsv) {
      return [];
    }
    return assignment.punchIdsCsv
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((id) => Number.isInteger(id) && id > 0);
  }

  showColumn(key: string): boolean {
    return this.displayColumns.includes(key);
  }

  getColumnStyle(key: string): Record<string, string> {
    const config = this.columnConfig[key];
    const style: Record<string, string> = {};
    if (config.width) style['width'] = config.width;
    if (config.minWidth) style['min-width'] = config.minWidth;
    return style;
  }
}
